import { type Monitor, untracked, useComputed } from "@suse-o11y/signal";
import type { GetFormOutput, SignalForm } from "@suse-o11y/signal/form";
import { nanoid } from "nanoid";
import React from "react";

import { useHandler } from "~/tools/use-handler";

import { FormContextProvider, type FormContextValue } from "./_context";

export interface FormProps<TForm extends SignalForm> extends Omit<React.HTMLAttributes<HTMLFormElement>, "onSubmit"> {
	state: undefined | TForm;
	onSubmit?: (values: GetFormOutput<TForm>, monitor: Monitor) => void | Promise<unknown>;
}

/**
 * A form that is powered by Signal Form.
 */
export const Form = React.forwardRef(
	({ state, id, onSubmit, onReset, ...props }, ref: React.ForwardedRef<HTMLFormElement>) => {
		const isSubmitting = useComputed((monitor) => state?.isSubmitting(monitor), [state]);
		const context = React.useMemo<FormContextValue>(() => ({ id: id ?? nanoid() }), [id]);
		const onSubmitStable = useHandler(onSubmit);

		React.useEffect(() => {
			if (onSubmitStable != null) {
				return state?.onSubmit((output) => untracked((monitor) => onSubmitStable(output, monitor)));
			}
		}, [state, onSubmitStable]);

		return (
			<FormContextProvider value={context}>
				<form
					aria-busy={isSubmitting || undefined}
					id={context.id}
					noValidate
					onReset={(event) => {
						event.preventDefault();
						state?.reset();
						onReset?.(event);
					}}
					onSubmit={(event) => {
						event.preventDefault();
						void state?.submit();
					}}
					ref={ref}
					{...props}
				/>
			</FormContextProvider>
		);
	},
) as {
	<TForm extends SignalForm>(props: FormProps<TForm>, ref: React.ForwardedRef<HTMLFormElement>): React.ReactElement;

	displayName?: string;
};

if (import.meta.env.DEV) {
	Form.displayName = "Form";
}
